import styled, { css } from 'styled-components';
import { Icon } from 'src/components';
import { overlayTextShadow } from '../sharedStyles';

export const Content = styled.div<{ $overlay: boolean }>(
  ({ theme, $overlay }) => css`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: ${$overlay ? theme.sz.s2 : theme.sz.s3};
    width: 100%;
  `,
);

export const PreviousAnswer = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: ${theme.sz.s3};
  `,
);

export const Item = styled.h2<{
  $showBigger: boolean;
  $overlay: boolean;
  $clickable?: boolean;
}>(
  ({ theme, $showBigger, $overlay, $clickable }) => css`
    ${$showBigger ? theme.typography.heading.desktop.l : theme.typography.heading.desktop.m};
    /* 3× heading.desktop.l (2.5rem) / .m (2rem) */
    font-size: ${$showBigger ? 7.5 : 6}rem;
    margin: 0;
    color: ${$overlay ? theme.co.neutral99 : theme.co.neutral10};
    overflow-wrap: anywhere;
    text-align: start;

    @media (max-width: ${theme.sz.lg}) {
      ${$showBigger ? theme.typography.heading.mobile.l : theme.typography.heading.mobile.m};
      /* 3× heading.mobile.l (2rem) / .m (1.5rem) */
      font-size: ${$showBigger ? 6 : 4.5}rem;
    }

    ${$overlay && overlayTextShadow};

    ${
      $clickable &&
      css`
        padding: 0;
        border: none;
        background: none;
        cursor: pointer;
        appearance: none;
      `
    }
  `,
);

export const Image = styled.img(
  ({ theme }) => css`
    max-width: ${theme.sz.xs};
    max-height: ${theme.sz.s48};
    border-radius: ${theme.bo.medium};
    object-fit: contain;
  `,
);

export const Definition = styled.p<{ $overlay: boolean }>(
  ({ theme, $overlay }) => css`
    ${$overlay ? theme.typography.paragraph.m : theme.typography.paragraph.base};
    margin: 0;
    color: ${$overlay ? theme.co.neutral99 : theme.co.neutral20};
    ${$overlay && overlayTextShadow};
  `,
);

export const DefinitionValue = styled.span<{ $overlay: boolean }>(
  ({ theme, $overlay }) => css`
    color: ${$overlay ? theme.co.neutral99 : theme.co.neutral10};
    font-weight: 700;
  `,
);

export const LiteralTranslation = styled.span<{ $overlay: boolean }>(
  ({ theme, $overlay }) => css`
    color: ${$overlay ? theme.co.neutral95 : theme.co.neutral40};
  `,
);

export const ExtraInfo = styled.dl<{ $overlay: boolean }>(
  ({ theme, $overlay }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.sz.s1};
    margin: 0;

    ${$overlay && overlayTextShadow};
  `,
);

export const ExtraInfoRow = styled.div<{ $overlay: boolean }>(
  ({ theme, $overlay }) => css`
    ${theme.typography.paragraph.m};
    display: flex;
    gap: ${theme.sz.s2};
    /* Light text over video so values (e.g. Furigana) stay readable on dark clips */
    color: ${$overlay ? theme.co.neutral99 : theme.co.neutral20};
  `,
);

export const ExtraInfoLabel = styled.dt<{ $overlay: boolean }>(
  ({ theme, $overlay }) => css`
    color: ${$overlay ? theme.co.neutral95 : theme.co.neutral40};
  `,
);

export const Actions = styled.div<{ $overlay: boolean }>(
  ({ theme, $overlay }) => css`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: ${$overlay ? theme.sz.s2 : theme.sz.s6};
    width: 100%;
    max-width: ${$overlay ? 'none' : theme.sz.xs};
    margin-top: ${$overlay ? theme.sz.s2 : theme.sz.s4};

    ${
      $overlay &&
      css`
        > button:first-child {
          width: 100%;
        }
      `
    }
  `,
);

export const SavedBadge = styled.span(
  ({ theme }) => css`
    ${theme.typography.heading.desktop.xxxs};
    display: inline-flex;
    align-items: center;
    gap: ${theme.sz.s1};
    padding: ${theme.sz.s05} ${theme.sz.s2};
    border: ${theme.bo.thinWidth} solid ${theme.co.primary20};
    border-radius: ${theme.bo.round};
    background-color: ${theme.co.primary95};
    color: ${theme.co.neutral10};
    font-size: ${theme.sz.s3};
  `,
);

export const SavedIcon = styled(Icon)(
  ({ theme }) => css`
    color: ${theme.co.primary0};
    font-size: ${theme.sz.s3};
  `,
);

export { Eyebrow, HeaderFlower } from '../sharedStyles';
